import React, { useEffect, useState } from "react";
import {
  fetchUserRole,
  fetchAllUserDailySummary,
  fetchUserDailySummary,
  fetchUserList,
  fetchDailySummaryByUsername,
  fetchAllLocations,
  fetchSalesRanking,
  fetchInventoryDashboard,
  fetchSalesByBrand,
  fetchHourlyWeekdayAverage,
  fetchMonthlySalesAverage,
  fetchKakaoSalesByCategory,
  fetchDashboardProductOptions,
  fetchProductHourlyWeekdayAverage,
  fetchProductMonthlySalesAverage,
} from "./logic/apiCall";
import "./css/home.css";
import SalesSummaryControl from "./custom/salessummary";
import SalesRankingControl from "./custom/salesrankings";
import InventoryInsights from "./custom/InventoryInsights";

const toLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const shiftDate = (date, days) => {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
};

const shiftRange = (range, days) => ({
  start: toLocalDate(shiftDate(new Date(`${range.start}T12:00:00`), days)),
  end: toLocalDate(shiftDate(new Date(`${range.end}T12:00:00`), days)),
});

const defaultTimeFilter = (base) => {
  const now = new Date();
  if (base === "monthly") return String(now.getMonth() + 1).padStart(2, "0");
  if (base === "yearly") return String(now.getFullYear());
  if (base === "hourly") return String(Math.min(21, Math.max(7, now.getHours())));
  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][now.getDay()];
};

function Home() {
  const [role, setRole] = useState("");
  const [summary, setSummary] = useState(null);
  const [comparisons, setComparisons] = useState(null);
  const [generalError, setGeneralError] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [rankingError, setRankingError] = useState("");
  const [inventoryError, setInventoryError] = useState("");
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [dateRange, setDateRange] = useState(() => {
    const date = toLocalDate(new Date());
    return { start: date, end: date };
  });
  const [timeBase, setTimeBase] = useState("weekday");
  const [timeFilter, setTimeFilter] = useState(() => defaultTimeFilter("weekday"));
  const [rankingData, setRankingData] = useState([]);
  const [summaryLocation, setSummaryLocation] = useState("all");
  const [rankingLocation, setRankingLocation] = useState("all");
  const [inventoryLocation, setInventoryLocation] = useState("all");
  const [inventoryData, setInventoryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [rankingLoading, setRankingLoading] = useState(true);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [brandData, setBrandData] = useState(null);
  const [brandLoading, setBrandLoading] = useState(true);
  const [brandError, setBrandError] = useState("");
  const [selectedSource, setSelectedSource] = useState("all");
  const [hourlyData, setHourlyData] = useState(null);
  const [hourlyLoading, setHourlyLoading] = useState(true);
  const [hourlyError, setHourlyError] = useState("");
  const [hourlyLocation, setHourlyLocation] = useState("all");
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(true);
  const [monthlyError, setMonthlyError] = useState("");
  const [monthlyLocation, setMonthlyLocation] = useState("all");
  const [categoryData, setCategoryData] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [categoryError, setCategoryError] = useState("");
  const [categoryLocation, setCategoryLocation] = useState("all");
  const [productHourlyData, setProductHourlyData] = useState(null);
  const [productHourlyLoading, setProductHourlyLoading] = useState(true);
  const [productHourlyError, setProductHourlyError] = useState("");
  const [productHourlyLocation, setProductHourlyLocation] = useState("all");
  const [dashboardProducts, setDashboardProducts] = useState([]);
  const [selectedDashboardProduct, setSelectedDashboardProduct] = useState("");
  const [productMonthlyData, setProductMonthlyData] = useState(null);
  const [productMonthlyLoading, setProductMonthlyLoading] = useState(true);
  const [productMonthlyError, setProductMonthlyError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    async function loadRoleUsersLocations() {
      try {
        const userRole = await fetchUserRole(controller.signal);
        setRole(userRole);
        if (userRole === "superadmin" || userRole === "admin") {
          const [usersList, locationsList] = await Promise.all([
            fetchUserList(controller.signal),
            fetchAllLocations(controller.signal),
          ]);
          setUsers([{ username: "all", name: "Todos" }, ...usersList]);
          setLocations([{ id: "all", name: "Todas" }, ...locationsList]);
        }
      } catch (err) {
        if (err.name !== "AbortError") setGeneralError(err.message);
      }
    }
    loadRoleUsersLocations();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!role) return undefined;
    const controller = new AbortController();
    const fetchForRange = (range) => {
      if (role === "cashier") return fetchUserDailySummary(range, controller.signal);
      if (selectedUser === "all") {
        return fetchAllUserDailySummary(summaryLocation, range, controller.signal);
      }
      return fetchDailySummaryByUsername(selectedUser, summaryLocation, range, controller.signal);
    };

    async function loadSummary() {
      setSummaryLoading(true);
      setSummaryError("");
      try {
        const [current, previousDay, previousWeek] = await Promise.all([
          fetchForRange(dateRange),
          fetchForRange(shiftRange(dateRange, -1)),
          fetchForRange(shiftRange(dateRange, -7)),
        ]);
        setSummary(current);
        setComparisons({ previousDay, previousWeek });
      } catch (err) {
        if (err.name !== "AbortError") setSummaryError(err.message);
      } finally {
        if (!controller.signal.aborted) setSummaryLoading(false);
      }
    }
    loadSummary();
    return () => controller.abort();
  }, [role, selectedUser, summaryLocation, dateRange]);

  useEffect(() => {
    if (!["admin", "superadmin"].includes(role)) return undefined;
    const controller = new AbortController();
    async function loadRanking() {
      setRankingLoading(true);
      setRankingError("");
      try {
        const interval = timeBase === "monthly" ? 12 : timeBase === "yearly" ? 5 : 30;
        const data = await fetchSalesRanking(
          timeBase,
          interval,
          rankingLocation,
          timeFilter,
          controller.signal
        );
        setRankingData(data);
      } catch (err) {
        if (err.name !== "AbortError") setRankingError("No se pudo cargar el ranking.");
      } finally {
        if (!controller.signal.aborted) setRankingLoading(false);
      }
    }
    loadRanking();
    return () => controller.abort();
  }, [role, timeBase, rankingLocation, timeFilter]);

  useEffect(() => {
    if (!["admin", "superadmin"].includes(role)) return undefined;
    const controller = new AbortController();
    async function loadInventory() {
      setInventoryLoading(true);
      setInventoryError("");
      try {
        setInventoryData(await fetchInventoryDashboard(inventoryLocation, controller.signal));
      } catch (err) {
        if (err.name !== "AbortError") setInventoryError(err.message);
      } finally {
        if (!controller.signal.aborted) setInventoryLoading(false);
      }
    }
    loadInventory();
    return () => controller.abort();
  }, [role, inventoryLocation]);

  useEffect(() => {
    if (!["admin", "superadmin"].includes(role)) return undefined;
    const controller = new AbortController();
    async function loadBrands() {
      setBrandLoading(true);
      setBrandError("");
      try {
        setBrandData(await fetchSalesByBrand(dateRange, controller.signal));
      } catch (err) {
        if (err.name !== "AbortError") setBrandError(err.message);
      } finally {
        if (!controller.signal.aborted) setBrandLoading(false);
      }
    }
    loadBrands();
    return () => controller.abort();
  }, [role, dateRange]);

  useEffect(() => {
    if (!["admin", "superadmin"].includes(role)) return undefined;
    const controller = new AbortController();
    async function loadHourlyAverage() {
      setHourlyLoading(true);
      setHourlyError("");
      try {
        setHourlyData(
          await fetchHourlyWeekdayAverage(dateRange, hourlyLocation, controller.signal)
        );
      } catch (err) {
        if (err.name !== "AbortError") setHourlyError(err.message);
      } finally {
        if (!controller.signal.aborted) setHourlyLoading(false);
      }
    }
    loadHourlyAverage();
    return () => controller.abort();
  }, [role, dateRange, hourlyLocation]);

  useEffect(() => {
    if (!["admin", "superadmin"].includes(role)) return undefined;
    const controller = new AbortController();
    async function loadMonthlyAverage() {
      setMonthlyLoading(true);
      setMonthlyError("");
      try {
        setMonthlyData(await fetchMonthlySalesAverage(monthlyLocation, controller.signal));
      } catch (err) {
        if (err.name !== "AbortError") setMonthlyError(err.message);
      } finally {
        if (!controller.signal.aborted) setMonthlyLoading(false);
      }
    }
    loadMonthlyAverage();
    return () => controller.abort();
  }, [role, monthlyLocation]);

  useEffect(() => {
    if (!["admin", "superadmin"].includes(role)) return undefined;
    const controller = new AbortController();
    async function loadCategories() {
      setCategoryLoading(true);
      setCategoryError("");
      try {
        setCategoryData(
          await fetchKakaoSalesByCategory(dateRange, categoryLocation, controller.signal)
        );
      } catch (err) {
        if (err.name !== "AbortError") setCategoryError(err.message);
      } finally {
        if (!controller.signal.aborted) setCategoryLoading(false);
      }
    }
    loadCategories();
    return () => controller.abort();
  }, [role, dateRange, categoryLocation]);

  useEffect(() => {
    if (!["admin", "superadmin"].includes(role)) return undefined;
    const controller = new AbortController();
    async function loadProductOptions() {
      setProductHourlyLoading(true);
      setProductHourlyError("");
      try {
        const products = await fetchDashboardProductOptions(productHourlyLocation, controller.signal);
        setDashboardProducts(products);
        setSelectedDashboardProduct((current) => (
          products.some((product) => product.code === current) ? current : products[0]?.code || ""
        ));
      } catch (err) {
        if (err.name !== "AbortError") setProductHourlyError(err.message);
      } finally {
        if (!controller.signal.aborted) setProductHourlyLoading(false);
      }
    }
    loadProductOptions();
    return () => controller.abort();
  }, [role, productHourlyLocation]);

  useEffect(() => {
    if (!["admin", "superadmin"].includes(role) || !selectedDashboardProduct) {
      setProductHourlyData(null);
      return undefined;
    }
    const controller = new AbortController();
    async function loadProductHourlyAverage() {
      setProductHourlyLoading(true);
      setProductHourlyError("");
      try {
        setProductHourlyData(await fetchProductHourlyWeekdayAverage(
          dateRange,
          selectedDashboardProduct,
          productHourlyLocation,
          controller.signal
        ));
      } catch (err) {
        if (err.name !== "AbortError") setProductHourlyError(err.message);
      } finally {
        if (!controller.signal.aborted) setProductHourlyLoading(false);
      }
    }
    loadProductHourlyAverage();
    return () => controller.abort();
  }, [role, dateRange, selectedDashboardProduct, productHourlyLocation]);

  useEffect(() => {
    if (!["admin", "superadmin"].includes(role) || !selectedDashboardProduct) {
      setProductMonthlyData(null);
      setProductMonthlyLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    async function loadProductMonthlyAverage() {
      setProductMonthlyLoading(true);
      setProductMonthlyError("");
      try {
        setProductMonthlyData(await fetchProductMonthlySalesAverage(
          selectedDashboardProduct,
          productHourlyLocation,
          controller.signal
        ));
      } catch (err) {
        if (err.name !== "AbortError") setProductMonthlyError(err.message);
      } finally {
        if (!controller.signal.aborted) setProductMonthlyLoading(false);
      }
    }
    loadProductMonthlyAverage();
    return () => controller.abort();
  }, [role, selectedDashboardProduct, productHourlyLocation]);

  const handleTimeBaseChange = (base) => {
    setTimeBase(base);
    setTimeFilter(defaultTimeFilter(base));
  };
  const isAdmin = role === "superadmin" || role === "admin";

  return (
    <main id="home-container">
      <header className="dashboard-heading">
        <div>
          <p className="dashboard-eyebrow">Panel operativo</p>
          <h1>Resumen del negocio</h1>
        </div>
      </header>

      {generalError && <div className="dashboard-alert" role="alert">{generalError}</div>}

      <SalesSummaryControl
        role={role}
        showFilters={isAdmin}
        summary={summary}
        comparisons={comparisons}
        loading={summaryLoading}
        error={summaryError}
        dateRange={dateRange}
        setDateRange={setDateRange}
        brandData={brandData}
        brandLoading={brandLoading}
        brandError={brandError}
        selectedSource={selectedSource}
        setSelectedSource={setSelectedSource}
        users={users}
        locations={locations}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        selectedLocation={summaryLocation}
        setSelectedLocation={setSummaryLocation}
        hourlyData={hourlyData}
        hourlyLoading={hourlyLoading}
        hourlyError={hourlyError}
        hourlyLocation={hourlyLocation}
        setHourlyLocation={setHourlyLocation}
        monthlyData={monthlyData}
        monthlyLoading={monthlyLoading}
        monthlyError={monthlyError}
        monthlyLocation={monthlyLocation}
        setMonthlyLocation={setMonthlyLocation}
        categoryData={categoryData}
        categoryLoading={categoryLoading}
        categoryError={categoryError}
        categoryLocation={categoryLocation}
        setCategoryLocation={setCategoryLocation}
        productHourlyData={productHourlyData}
        productHourlyLoading={productHourlyLoading}
        productHourlyError={productHourlyError}
        productHourlyLocation={productHourlyLocation}
        setProductHourlyLocation={setProductHourlyLocation}
        dashboardProducts={dashboardProducts}
        selectedDashboardProduct={selectedDashboardProduct}
        setSelectedDashboardProduct={setSelectedDashboardProduct}
        productMonthlyData={productMonthlyData}
        productMonthlyLoading={productMonthlyLoading}
        productMonthlyError={productMonthlyError}
      />

      {isAdmin && (
        <section className="dashboard-grid">
            <SalesRankingControl
              selectedLocation={rankingLocation}
              setSelectedLocation={setRankingLocation}
              locations={locations}
              error={rankingError}
              loading={rankingLoading}
              timeBase={timeBase}
              setTimeBase={handleTimeBaseChange}
              rankingData={rankingData}
              timeFilter={timeFilter}
              setTimeFilter={setTimeFilter}
            />
            <InventoryInsights
              data={inventoryData}
              loading={inventoryLoading}
              error={inventoryError}
              locations={locations}
              selectedLocation={inventoryLocation}
              setSelectedLocation={setInventoryLocation}
            />
        </section>
      )}
    </main>
  );
}

export default Home;
